import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import type { DealApi } from '@openx/types/deal.ts';
import { BUDGET_TYPE, type Cadence, MONETIZING_FIELDS } from '@openx/types/monetizing.ts';
import { priceFormat } from '@openx/utils/lib/priceFormat.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DealDetailsSectionProps } from '../../../types.ts';
import { pacingRegionLabels } from '../../DealForm/sections/PacingSection/pacingLabels.ts';
import { useDealContext } from '../../utils/context/DealContext.tsx';

export type DealPacingSectionDeal = Pick<
  DealApi,
  'pmp_deal_type' | 'start_date' | 'end_date' | 'monetizing_uid' | 'budget_type'
>;

export function DealPacingSection<T extends DealPacingSectionDeal>({
  deal,
  historyMode,
}: Omit<DealDetailsSectionProps<T>, 'showPacing'>) {
  const { t } = useTranslation();
  const { monetizing_uid, start_date, end_date } = deal;

  const { useMonetizingFetch } = useDealContext();

  const { isLoading, monetizing } = useMonetizingFetch(monetizing_uid);

  const isSpend = monetizing?.[MONETIZING_FIELDS.BUDGET_TYPE] === BUDGET_TYPE.SPEND;
  const isCadence = !monetizing?.[MONETIZING_FIELDS.BUDGET];
  const isDaily = (monetizing as Cadence)?.cadences?.[0]?.[MONETIZING_FIELDS.CADENCE_TYPE] === 'daily';

  const budget = useMemo(() => {
    const budgetValue = monetizing?.[MONETIZING_FIELDS.BUDGET];
    const budgetCadenceValue = (monetizing as Cadence)?.cadences?.[0]?.[MONETIZING_FIELDS.BUDGET];

    if (budgetValue) {
      if (isSpend) {
        return priceFormat(budgetValue, 'USD')?.toString();
      }
      return new Intl.NumberFormat('en-US').format(budgetValue);
    }

    if (budgetCadenceValue) {
      if (isSpend) {
        return priceFormat(budgetCadenceValue, 'USD')?.toString();
      }
      return new Intl.NumberFormat('en-US').format(budgetCadenceValue);
    }

    return null;
  }, [monetizing, isSpend]);

  return (
    <FormSection
      divider
      historyMode={historyMode}
      title={t('Pacing and Duration')}
      description={t('A metering tool that controls how quickly (or not) a deal monetizes.')}
    >
      {!monetizing_uid && <FieldDetails toggle title={t('Enable Pacing')} value={!!monetizing_uid} />}
      {isLoading ? (
        <Loader />
      ) : (
        !!monetizing && (
          <>
            {isSpend && <FieldDetails title={t('Pacing Budget Currency')} value={'USD'} />}
            <FieldDetails title={t('Region')} value={pacingRegionLabels[monetizing[MONETIZING_FIELDS.REGION]]} />
            <FieldDetails title={t('Goal')} value={isSpend ? t('Budget Spend') : t('Impressions')} />
            <FieldDetails title={t('Budgeting Mode')} value={isCadence ? t('Cadence') : t('Campaign Flight Time')} />
            <IndentedSections sx={{ mb: 2 }}>
              {isSpend ? (
                <FieldDetails title={isCadence ? t('Budget') : t('Campaign Budget')} value={budget} />
              ) : (
                <FieldDetails title={t('Impression Goal')} value={budget} />
              )}
              {isCadence && (
                <FieldDetails
                  title={t('Cadence Type')}
                  value={isDaily ? t('Daily (00:00 AM - 11:59 PM)') : t('Weekly (Monday - Sunday)')}
                />
              )}
            </IndentedSections>

            <FieldDetails
              title={t('Executing Model')}
              value={monetizing[MONETIZING_FIELDS.PACING_MODE] === 'asap' ? t('ASAP') : t('Evenly')}
            />
          </>
        )
      )}
      <Grid
        container
        sx={{
          mb: 2,
          mt: monetizing ? 2 : 0,
        }}
      >
        {!historyMode && (
          <Grid
            size={12}
            sx={{
              mb: 1,
            }}
          >
            <Typography variant="h3" data-test="pacing-duration-label">
              {t('Duration')}
            </Typography>
          </Grid>
        )}
        <Grid size={3}>
          <FieldDetails title={t('Start Date')} value={start_date} dateFormat={'yyyy-MM-dd hh:mm a'} />
        </Grid>
        <FieldDetails title={t('End Date')} value={end_date} dateFormat={'yyyy-MM-dd hh:mm a'} />
      </Grid>
    </FormSection>
  );
}
